import { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";

import SelectMenu from "./crm/SelectMenu";
const DEGREE_OPTIONS = [
  { value: "", label: "Select Law Degree" },
  { value: "B.Sc.LLB", label: "B.Sc.LLB" },
  { value: "BBA.LLB", label: "BBA.LLB" },
  { value: "B.Com.LLB", label: "B.Com.LLB" },
  { value: "B.A.LLB", label: "B.A.LLB" },
  { value: "LLB", label: "LLB" },
  { value: "Other", label: "Other" },
];

const USER_CATEGORY_OPTIONS = [
  { value: "", label: "Select User Type" },
  { value: "Lawyer", label: "Lawyer" },
  { value: "Law student", label: "Law student" },
  { value: "Advocate", label: "Advocate" },
];

const FIRM_OPTIONS = [
  { value: "", label: "Choose One" },
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// Check if a lawyer's profile is incomplete
export function isProfileIncomplete(data) {
  if (!data) return true;
  if (!data.name?.trim()) return true;
  if (!data.degree?.trim()) return true;

  const category = data.userCategory || data.userCateogry || "";
  if (!category) return true;

  if (category === "Lawyer" || category === "Advocate") {
    if (!data.lawyerId?.trim()) return true;
    if (!data.type?.trim()) return true;
    const firm = data.representsFirm || data.isFirm || "";
    if (!firm) return true;
    if (firm === "Yes" && !data.firmName?.trim()) return true;
  } else if (category === "Law student") {
    if (!data.registrationNumber?.trim()) return true;
    if (!data.collegeName?.trim()) return true;
  }

  return false;
}

export default function ProfileCompletionPopup({ isOpen, onComplete }) {
  const { globalUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    degree: "",
    userCategory: "",
    lawyerId: "",
    type: "",
    representsFirm: "",
    firmName: "",
    registrationNumber: "",
    collegeName: "",
  });

  // Load existing data
  useEffect(() => {
    if (!isOpen || !globalUser?.uid) return;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "lawyers", globalUser.uid));
        if (snap.exists()) {
          const d = snap.data();
          setFormData((prev) => ({
            ...prev,
            name: d.name || "",
            degree: d.degree || "",
            userCategory: d.userCategory || d.userCateogry || "",
            lawyerId: d.lawyerId || "",
            type: d.type || "",
            representsFirm: d.representsFirm || d.isFirm || "",
            firmName: d.firmName || "",
            registrationNumber: d.registrationNumber || "",
            collegeName: d.collegeName || "",
          }));
        }
      } catch (err) {
        console.error("Error loading profile data:", err);
      }
    };
    load();
  }, [isOpen, globalUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "lawyerId" ? value.toUpperCase() : value,
    }));
  };

  const isFormValid = () => {
    if (!formData.name.trim()) return false;
    if (!formData.degree) return false;
    if (!formData.userCategory) return false;

    if (formData.userCategory === "Lawyer" || formData.userCategory === "Advocate") {
      if (!formData.lawyerId.trim()) return false;
      if (!formData.type.trim()) return false;
      if (!formData.representsFirm) return false;
      if (formData.representsFirm === "Yes" && !formData.firmName.trim()) return false;
    } else if (formData.userCategory === "Law student") {
      if (!formData.registrationNumber.trim()) return false;
      if (!formData.collegeName.trim()) return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!isFormValid() || !globalUser?.uid) return;
    setSaving(true);
    try {
      const saveData = {
        name: formData.name,
        degree: formData.degree,
        userCategory: formData.userCategory,
        userCateogry: formData.userCategory,
        type: formData.type,
        lawyerId: formData.lawyerId,
        representsFirm: formData.representsFirm,
        isFirm: formData.representsFirm,
        firmName: formData.firmName,
        registrationNumber: formData.registrationNumber,
        collegeName: formData.collegeName,
        profileCompleted: true,
      };
      await setDoc(doc(db, "lawyers", globalUser.uid), saveData, { merge: true });
      toast.success("Profile completed successfully!");
      onComplete();
    } catch (err) {
      console.error("Error saving profile:", err);
      toast.error("Failed to save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputClass =
    "w-full bg-gray-900 border border-gray-600 rounded-lg py-2.5 px-4 text-white text-sm focus:outline-none focus:border-yellow-500 transition-colors";
  const selectClass =
    "w-full bg-gray-900 border border-gray-600 rounded-lg py-2.5 px-4 text-white text-sm focus:outline-none focus:border-yellow-500 appearance-none transition-colors [&>option]:bg-gray-900 [&>option]:text-white";
  const labelClass = "block text-gray-400 mb-1 text-sm";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999] p-4">
      <div className="bg-[#1E1E45] rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#1E1E45] px-6 pt-6 pb-4 border-b border-gray-700 rounded-t-2xl">
          <h2 className="text-xl font-bold text-yellow-500 text-center">
            Complete Your Profile
          </h2>
          <p className="text-gray-400 text-sm text-center mt-1">
            Please fill in all required details to continue
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Name */}
          <div>
            <label className={labelClass}>
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className={inputClass}
            />
          </div>

          {/* Law Degree */}
          <div>
            <label className={labelClass}>
              Law Degree <span className="text-red-500">*</span>
            </label>
            <SelectMenu
              name="degree"
              value={formData.degree}
              onChange={handleChange}
              className={selectClass}
            >
              {DEGREE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </SelectMenu>
          </div>

          {/* User Category */}
          <div>
            <label className={labelClass}>
              Choose User Type <span className="text-red-500">*</span>
            </label>
            <SelectMenu
              name="userCategory"
              value={formData.userCategory}
              onChange={(e) => {
                handleChange(e);
                // Reset dependent fields
                setFormData((prev) => ({
                  ...prev,
                  userCategory: e.target.value,
                  lawyerId: "",
                  type: "",
                  representsFirm: "",
                  firmName: "",
                  registrationNumber: "",
                  collegeName: "",
                }));
              }}
              className={selectClass}
            >
              {USER_CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </SelectMenu>
          </div>

          {/* Lawyer / Advocate fields */}
          {(formData.userCategory === "Lawyer" || formData.userCategory === "Advocate") && (
            <>
              <div>
                <label className={labelClass}>
                  Enrollment ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lawyerId"
                  value={formData.lawyerId}
                  onChange={handleChange}
                  placeholder="Enter Enrollment ID"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Practicing Field <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  placeholder="e.g. Criminal, Civil, Corporate"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Are you representing Law/Professional Firm? <span className="text-red-500">*</span>
                </label>
                <SelectMenu
                  name="representsFirm"
                  value={formData.representsFirm}
                  onChange={handleChange}
                  className={selectClass}
                >
                  {FIRM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </SelectMenu>
              </div>

              {formData.representsFirm === "Yes" && (
                <div>
                  <label className={labelClass}>
                    Firm Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firmName"
                    value={formData.firmName}
                    onChange={handleChange}
                    placeholder="Enter firm name"
                    className={inputClass}
                  />
                </div>
              )}
            </>
          )}

          {/* Law Student fields */}
          {formData.userCategory === "Law student" && (
            <>
              <div>
                <label className={labelClass}>
                  Registration Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="registrationNumber"
                  value={formData.registrationNumber}
                  onChange={handleChange}
                  placeholder="Enter Registration Number"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  College Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="collegeName"
                  value={formData.collegeName}
                  onChange={handleChange}
                  placeholder="Enter College Name"
                  className={inputClass}
                />
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-[#1E1E45] px-6 py-4 border-t border-gray-700 rounded-b-2xl">
          <button
            onClick={handleSave}
            disabled={!isFormValid() || saving}
            className={`w-full py-3 rounded-lg font-semibold text-sm transition-colors ${
              isFormValid() && !saving
                ? "bg-yellow-500 hover:bg-yellow-400 text-black"
                : "bg-gray-600 text-gray-400 cursor-not-allowed"
            }`}
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              "Save & Continue"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
